import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import {
  certificatesApi,
  QueryCertificateParams,
  QueryMaterialExpiryParams,
  CreateCertificateDto,
  UpdateCertificateDto,
  LinkItemDto,
  CreateMaterialExpiryDto,
  UpdateMaterialExpiryDto,
} from '../services/certificates.api';
import { toast } from 'sonner';
import { getErrorMessage } from '../client';

/**
 * Query hook for fetching paginated certificates list
 */
export function useCertificatesList(params: QueryCertificateParams = {}) {
  return useQuery({
    queryKey: queryKeys.certificates.list(params),
    queryFn: () => certificatesApi.getAll(params),
  });
}

/**
 * Query hook for fetching a single certificate
 */
export function useCertificate(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.certificates.detail(id || ''),
    queryFn: () => certificatesApi.getById(id!),
    enabled: !!id,
  });
}

/**
 * Query hook for fetching certificates dashboard data
 */
export function useCertificatesDashboard() {
  return useQuery({
    queryKey: [...queryKeys.certificates.all, 'dashboard'],
    queryFn: () => certificatesApi.getDashboard(),
  });
}

/**
 * Query hook for fetching expiring certificates
 */
export function useExpiringCertificates(days?: number) {
  return useQuery({
    queryKey: [...queryKeys.certificates.all, 'expiring', days],
    queryFn: () => certificatesApi.getExpiring(days),
  });
}

/**
 * Query hook for fetching material expiries
 */
export function useMaterialExpiries(params: QueryMaterialExpiryParams = {}) {
  return useQuery({
    queryKey: [...queryKeys.certificates.all, 'materialExpiry', params],
    queryFn: () => certificatesApi.getMaterialExpiries(params),
  });
}

/**
 * Query hook for searching certificates by heat number
 */
export function useCertificatesByHeatNumber(heatNumber: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.certificates.all, 'heatNumber', heatNumber],
    queryFn: () => certificatesApi.searchByHeatNumber(heatNumber!),
    enabled: !!heatNumber,
  });
}

/**
 * Query hook for searching certificates by lot number
 */
export function useCertificatesByLotNumber(lotNumber: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.certificates.all, 'lotNumber', lotNumber],
    queryFn: () => certificatesApi.searchByLotNumber(lotNumber!),
    enabled: !!lotNumber,
  });
}

/**
 * Mutation hook for creating a certificate
 */
export function useCreateCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCertificateDto) => certificatesApi.create(data),
    onSuccess: () => {
      toast.success('Certificate created successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.certificates.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for creating a certificate with file upload
 */
export function useCreateCertificateWithFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, file }: { data: CreateCertificateDto; file?: File }) =>
      certificatesApi.createWithFile(data, file),
    onSuccess: () => {
      toast.success('Certificate created successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.certificates.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for updating a certificate
 */
export function useUpdateCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCertificateDto }) =>
      certificatesApi.update(id, data),
    onSuccess: (_, variables) => {
      toast.success('Certificate updated successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.certificates.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.certificates.detail(variables.id) });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for deleting a certificate
 */
export function useDeleteCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => certificatesApi.delete(id),
    onSuccess: () => {
      toast.success('Certificate deleted successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.certificates.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for linking an item to a certificate
 */
export function useLinkItemToCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ certificateId, data }: { certificateId: string; data: LinkItemDto }) =>
      certificatesApi.linkItem(certificateId, data),
    onSuccess: (_, variables) => {
      toast.success('Item linked to certificate');
      queryClient.invalidateQueries({
        queryKey: queryKeys.certificates.detail(variables.certificateId),
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for unlinking an item from a certificate
 */
export function useUnlinkItemFromCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ certificateId, itemId }: { certificateId: string; itemId: string }) =>
      certificatesApi.unlinkItem(certificateId, itemId),
    onSuccess: (_, variables) => {
      toast.success('Item unlinked from certificate');
      queryClient.invalidateQueries({
        queryKey: queryKeys.certificates.detail(variables.certificateId),
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for linking a batch to a certificate
 */
export function useLinkBatchToCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      certificateId,
      data,
    }: {
      certificateId: string;
      data: {
        batchNumber: string;
        itemId: string;
        quantity: number;
        heatNumber?: string;
        lotNumber?: string;
        notes?: string;
      };
    }) => certificatesApi.linkBatch(certificateId, data),
    onSuccess: (_, variables) => {
      toast.success('Batch linked to certificate');
      queryClient.invalidateQueries({
        queryKey: queryKeys.certificates.detail(variables.certificateId),
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for validating a batch against certificates
 */
export function useValidateBatch() {
  return useMutation({
    mutationFn: (batchNumber: string) => certificatesApi.validateBatch(batchNumber),
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for deleting a certificate document
 */
export function useDeleteCertificateDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => certificatesApi.deleteDocument(id),
    onSuccess: (_, id) => {
      toast.success('Document deleted successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.certificates.detail(id) });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for creating a material expiry record
 */
export function useCreateMaterialExpiry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMaterialExpiryDto) => certificatesApi.createMaterialExpiry(data),
    onSuccess: () => {
      toast.success('Material expiry record created');
      queryClient.invalidateQueries({ queryKey: queryKeys.certificates.all });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for updating a material expiry record
 */
export function useUpdateMaterialExpiry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMaterialExpiryDto }) =>
      certificatesApi.updateMaterialExpiry(id, data),
    onSuccess: () => {
      toast.success('Material expiry record updated');
      queryClient.invalidateQueries({ queryKey: queryKeys.certificates.all });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
